import { Icon } from '@components/primitives/Icon'
import Link from '@components/primitives/Link'
import Modal from '@components/primitives/Modal/index.client'
import { classMerge } from '@components/utilities/classMerge'
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons/faChevronDown'
import Routes from '@lib/routes'
import { useState } from 'react'

export default function CookiePolicyModal({
  onClose,
  visible,
}: {
  onClose: () => void
  visible: boolean
}) {
  const [isPersonalizeExpanded, setIsPersonalizeExpanded] = useState(false)
  const [isSavePreferencesExpanded, setIsSavePreferencesExpanded] =
    useState(false)
  const [
    isUnderstandHowTheSiteIsUsedExpanded,
    setIsUnderstandHowTheSiteIsUsedExpanded,
  ] = useState(false)

  return (
    <Modal
      bodyClassName={'px-8'}
      onClose={onClose}
      size='Medium'
      title='Cookies &amp; Data Use Policy'
      titleContainerClassName='m-0 px-3'
      titleTextClassName='!text-xl text-left font-bold text-black'
      titleTextStyleName='h3'
      visible={visible}
    >
      <div className='flex flex-col gap-8'>
        <p className='text-left text-base text-neutral600'>
          At Power, we believe in using data responsibly to help you find the
          right clinical trials &mdash; without compromising your privacy. This
          page explains how we use cookies and personal data across{' '}
          <Link
            className='text-primary600 underline'
            href='https://www.withpower.com'
            newTab
          >
            www.withpower.com
          </Link>
          .
        </p>
        <div className='flex flex-col gap-6'>
          <h2 className='text-left text-lg font-semibold text-black'>
            Before You Create a Profile
          </h2>
          <p className='text-left text-base text-neutral600'>
            When you browse Power&apos;s website, you&apos;re opting in to our
            use of cookies. Cookies are used to improve your experience and help
            us understand how the site is used so that we can make improvements
            for you in the future. Specifically, we use cookies to:
          </p>
          <div className='flex flex-col gap-y-2'>
            <h3
              className='flex cursor-pointer items-center gap-2 pl-2 text-left text-base font-semibold text-black'
              onClick={() => setIsPersonalizeExpanded(!isPersonalizeExpanded)}
            >
              <Icon
                className={classMerge(
                  'transform text-primary600 transition-all duration-300',
                  isPersonalizeExpanded && 'rotate-180',
                )}
                icon={faChevronDown}
                size='1x'
              />
              Personalize Your Experience
            </h3>
            <div
              className={
                isPersonalizeExpanded
                  ? 'mb-2 flex flex-col gap-2 py-2'
                  : 'hidden'
              }
            >
              <p className='text-left text-base text-neutral600'>
                We use cookies to customize your visit based on basic
                information like your general location (determined by your IP
                address). This allows us to:
              </p>
              <ul className='flex list-disc flex-col gap-1 pl-8 text-base text-neutral600'>
                <li>
                  Show you clinical trials that are geographically relevant to
                  you
                </li>
                <li>
                  Tailor search results to match the conditions or keywords
                  you&apos;ve explored before
                </li>
                <li>
                  Pre-fill certain fields or remember your previous searches, so
                  you don&apos;t have to repeat them
                </li>
              </ul>
            </div>
            <h3
              className='flex cursor-pointer items-center gap-2 pl-2 text-left text-base font-semibold text-black'
              onClick={() =>
                setIsSavePreferencesExpanded(!isSavePreferencesExpanded)
              }
            >
              <Icon
                className={classMerge(
                  'transform text-primary600 transition-all duration-300',
                  isSavePreferencesExpanded && 'rotate-180',
                )}
                icon={faChevronDown}
                size='1x'
              />
              Save Your Preferences
            </h3>
            <div
              className={
                isSavePreferencesExpanded
                  ? 'mb-2 flex flex-col gap-2 py-2'
                  : 'hidden'
              }
            >
              <p className='text-left text-base text-neutral600'>
                We remember what you interact with during your visit &mdash; for
                example:
              </p>
              <ul className='flex list-disc flex-col gap-1 pl-8 text-base text-neutral600'>
                <li>The conditions you search for</li>
                <li>
                  Whether you prefer certain types of studies (e.g., paid
                  trials, trials for a specific age group)
                </li>
                <li>
                  Your sorting or filtering preferences when browsing trials
                </li>
              </ul>
              <p className='text-left text-base text-neutral600'>
                This helps us make your experience more efficient and
                personalized the next time you visit.
              </p>
            </div>
            <h3
              className='flex cursor-pointer items-center gap-2 pl-2 text-left text-base font-semibold text-black'
              onClick={() =>
                setIsUnderstandHowTheSiteIsUsedExpanded(
                  !isUnderstandHowTheSiteIsUsedExpanded,
                )
              }
            >
              <Icon
                className={classMerge(
                  'transform text-primary600 transition-all duration-300',
                  isUnderstandHowTheSiteIsUsedExpanded && 'rotate-180',
                )}
                icon={faChevronDown}
                size='1x'
              />
              Understand How the Site Is Used
            </h3>
            <div
              className={
                isUnderstandHowTheSiteIsUsedExpanded
                  ? 'mb-2 flex flex-col gap-2 py-2'
                  : 'hidden'
              }
            >
              <p className='text-left text-base text-neutral600'>
                Cookies help us collect anonymous usage data so we can make
                Power better. We use these insights to:
              </p>
              <ul className='mb-2 flex list-disc flex-col gap-1 pl-8 text-base text-neutral600'>
                <li>
                  Monitor how users move through the site &mdash; for example,
                  which pages get the most traffic and where users tend to exit
                </li>
                <li>
                  Track how long visitors stay on each page and whether they
                  find what they’re looking for
                </li>
                <li>
                  Identify points of friction or confusion so we can improve
                  usability
                </li>
                <li>
                  Test design changes (like different page layouts or buttons)
                  and measure which version performs better
                </li>
                <li>
                  Detect and fix bugs or slow-loading pages to maintain site
                  reliability
                </li>
              </ul>
            </div>
          </div>
          <p className='text-left text-base text-neutral600'>
            These analytics are aggregated and do not include personal
            identifiers. We use tools like Google Analytics to process this
            data, but we don&apos;t use it to target ads or sell your
            information.
          </p>
          <div className='flex flex-col gap-2'>
            <p className='text-left text-base text-neutral600'>
              <b className='font-bold'>We do not:</b>
            </p>
            <ul className='list-disc pl-5 text-base text-neutral600'>
              <li>Sell or share your personal data with advertisers</li>
              <li>
                Use your behavior on our site to target you with third-party ads
              </li>
            </ul>
          </div>
          <p className='text-left text-base text-neutral600'>
            All cookie use is designed to support your experience on Power
            &mdash; never to track you across the internet or monetize your
            information.
          </p>
        </div>
        <div className='flex flex-col gap-6'>
          <h2 className='text-left text-lg font-semibold text-black'>
            After You Create a Profile
          </h2>
          <p className='text-left text-base text-neutral600'>
            When you sign up for a Power account, you agree to our{' '}
            <Link
              className='text-primary600 underline'
              href={Routes.privacyPolicy}
              newTab
            >
              Privacy Policy
            </Link>{' '}
            and{' '}
            <Link
              className='text-primary600 underline'
              href={Routes.termsOfService}
              newTab
            >
              Terms of Service
            </Link>
            . Creating a profile allows us to better serve you by tailoring the
            platform to your specific needs.
          </p>
          <p className='text-left text-base text-neutral600'>
            Once you create a profile:
          </p>
          <ul className='list-disc pl-5 text-base text-neutral600'>
            <li>
              We may collect additional information about your health and
              clinical interests to help us match you to the most relevant
              studies.
            </li>
            <li>
              We continue to use cookies to remember your session, keep you
              logged in, and personalize your dashboard.
            </li>
            <li>
              You have full control &mdash; you can delete your profile at any
              time, and we&apos;ll remove your personal data in accordance with
              our privacy practices.
            </li>
          </ul>
          <p className='text-left text-base text-neutral600'>
            We use your data solely to fulfill our mission: helping you find
            clinical trials that could be a fit &mdash; not for advertising or
            resale.
          </p>
        </div>
      </div>
    </Modal>
  )
}
